import * as constants from '../constants';
import { LogInternally } from '../LogInternally';
import * as types from '../types';
import { isRecordType, pruneContext } from '../util';

/**
 * An abstract class that represents the configuration options and properties for a given
 * logger instance, {@link types.ILogger}.  All logger instances should extend this class, either
 * directly or indirectly via another class.
 */
export abstract class LoggerConfiguration
  extends LogInternally
  implements types.ILoggerConfiguration
{
  protected readonly options: types.LoggerOptions;
  private readonly _name: string | undefined;

  constructor(options: { readonly name?: string } & types.LoggerOptions) {
    super();
    this.options = options;
    this._name = options.name;
  }

  public formatMessage(level: types.LogLevel, message: string): string {
    const prefix = isRecordType(this.options.messagePrefix)
      ? this.options.messagePrefix[level]
      : this.options.messagePrefix;

    const formatter = isRecordType(this.options.messageFormatter)
      ? this.options.messageFormatter[level]
      : this.options.messageFormatter;

    const prefixedMessage = prefix ? `${prefix}${message}` : message;
    return formatter ? formatter(prefixedMessage) : prefixedMessage;
  }

  /**
   * The application or package name that the logger instance, {@link types.ILogger}, was configured
   * with.
   */
  public get appName() {
    return this.options.appName ?? process.env.APP_NAME ?? 'unknown';
  }

  /**
   * The application version that the logger instance, {@link types.ILogger}, was configured with.
   */
  public get appVersion() {
    return this.options.appVersion ?? process.env.APP_VERSION ?? 'unknown';
  }

  /**
   * Returns the default context, {@link types.LogContext} that will be included in the log
   * messages emitted by each strategy, {@link types.IStrategy}, that the logger instance,
   * {@link types.ILogger}, was configured with.
   */
  public get context(): types.ProcessedLogContext {
    return {
      ...pruneContext({ ...this.globalContext, ddtags: this.ddtags }),
      appName: this.appName,
      appVersion: this.appVersion,
      environmentName: this.environmentName,
      name: this.name,
    };
  }

  public get ddtags() {
    return this.options.ddtags;
  }

  /**
   * The name of the environment that the logger instance, {@link types.ILogger}, was configured
   * with.  The environment name is used to add contextual data to logs that are emitted by the
   * logger's configured strategies, {@link types.IStrategy}.
   */
  public get environmentName() {
    return this.options.environmentName ?? process.env.NODE_ENV ?? 'development';
  }

  /**
   * Optional, additional context parameters, {@link types.LogContext}, that the logger instance is
   * configured with.  These context parameters will be included in all logs that are emitted by
   * the logger.
   */
  protected get globalContext() {
    return pruneContext(this.options.globalContext ?? {});
  }

  protected get internalMessagePrefix() {
    return this.name;
  }

  /**
   * Whether or not the logger instance is enabled, based on how the logger is configured.  When the
   * logger is not enabled, logs will not propagate through to any of the logger's underlying
   * strategies, {@link types.IStrategy}.
   *
   * Logs will also not propagate through to individual strategies, {@link types.IStrategy}, if
   * that strategy is disabled even when the overall logger instance, {@link types.ILogger}, is
   * enabled.
   */
  public get isEnabled() {
    return this.options.isEnabled ?? true;
  }

  /**
   * The {@link types.LogLevel} that the logger is configured to use.
   */
  public get level(): types.LogLevel {
    if (this.options.level) {
      return this.options.level;
    }
    const processEnvLogLevel = process.env.LOG_LEVEL;
    if (processEnvLogLevel) {
      if (types.isLogLevel(processEnvLogLevel)) {
        return processEnvLogLevel;
      }
      this.logInternally(
        types.LogLevel.Warn,
        `Detected unsupported log level in process.env: '${process.env.LOG_LEVEL}'. ` +
          `Defaulting to '${constants.LoggerOptionDefaults.level}'.`,
      );
    }
    return constants.LoggerOptionDefaults.level;
  }

  public get name() {
    return this._name ?? this.appName;
  }

  /**
   * Whether or not pretty formatting should be used when logging information in either server
   * or client contexts.
   *
   * When the strategy {@link PinoStrategy} is used, this option will control whether or not
   * the "pino-pretty" package is used to format log information on the server, and in client
   * contexts, it controls whether or not manual colorization is applied to log messages that are
   * emitted via the browser.
   *
   * When the strategy {@link ConsoleStrategy} is used, this option controls whether or not manual
   * colorization is applied to log messages that are emitted via the traditional {@link console}
   * object.
   */
  public get pretty() {
    return this.options.pretty ?? constants.LoggerOptionDefaults.pretty;
  }

  /**
   * Returns the strategy instances, {@link types.IStrategy}, that the logger instance is configured
   * with.
   *
   * @see {@link types.IStrategy}
   */
  protected get strategies() {
    return this.options.strategies ?? [];
  }

  /**
   * The time formatting token {@link string} or options {@link DateTimeFormatOptions} that should
   * be used to format the time in which the log was emitted when logs are issued via the
   * traditional console object.
   */
  public get timeFormat() {
    return this.options.timeFormat ?? 'LLL dd yyyy HH:mm:ss';
  }
}
